import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getErrorMessage } from '../../../lib/apiClient';
import { formatBytes, formatDate } from '../../../utils/format';
import { getFileDetails, updateFileMetadata } from '../filesApi';
import { fileTypeLabel, folderPathLabel, relativeTime } from '../fileMeta';

/** One label/value pair in the About grid. */
function Fact({ label, children }) {
  return (
    <>
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{children}</dd>
    </>
  );
}

/** Placeholder bar standing in for a value still being fetched. */
function Pending() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block h-4 w-32 animate-pulse rounded bg-slate-200 align-middle"
    />
  );
}

/** A person as name over email; an unresolvable one reads as a dash. */
function Person({ person }) {
  if (!person) return '—';
  return (
    <span className="block">
      <span className="block">{person.name}</span>
      <span className="block text-xs text-slate-500">{person.email}</span>
    </span>
  );
}

/** Timestamp with the "3 hours ago" hint the absolute form doesn't give. */
function Timestamp({ iso }) {
  if (!iso) return '—';
  const relative = relativeTime(iso);
  return (
    <span className="block">
      <span className="block">{formatDate(iso)}</span>
      {relative && <span className="block text-xs text-slate-500">{relative}</span>}
    </span>
  );
}

/** Monospace value with a copy button — for the id and checksum. */
function CopyableValue({ label, value }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className="flex items-start gap-2">
      <span className="min-w-0 flex-1 break-all font-mono text-xs text-slate-600">{value}</span>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch {
            toast.error('Could not copy');
          }
        }}
        className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

const countLabel = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "3 people · 1 link", or "Not shared". */
function sharingLabel(shared) {
  if (!shared?.isShared) return 'Not shared';
  const parts = [];
  if (shared.peopleCount)
    parts.push(countLabel(shared.peopleCount, 'person').replace('persons', 'people'));
  if (shared.linkCount) parts.push(countLabel(shared.linkCount, 'link'));
  return parts.join(' · ');
}

/**
 * The file Information panel. Facts already carried by the folder listing row
 * paint immediately; the joined ones (people, versions, sharing, location)
 * stream in from `GET /files/:id/details` and show a placeholder until they do.
 * A failed details request degrades to the row-carried facts rather than
 * blocking the tags/description editor below.
 */
export default function MetadataModal({ open, onClose, target, commentCount = 0 }) {
  const queryClient = useQueryClient();
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const item = target?.item;

  useEffect(() => {
    const meta = item?.metadata ?? {};
    setTags(Array.isArray(meta.tags) ? meta.tags.join(', ') : '');
    setDescription(typeof meta.description === 'string' ? meta.description : '');
    setShowAdvanced(false);
  }, [target, item]);

  const {
    data: details,
    isPending: detailsPending,
    isError: detailsFailed,
  } = useQuery({
    queryKey: ['file-details', item?.id],
    queryFn: () => getFileDetails(item.id),
    enabled: Boolean(open && item?.id),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      updateFileMetadata({
        id: item.id,
        metadata: {
          tags: tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 32),
          description: description.trim(),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      queryClient.invalidateQueries({ queryKey: ['file-details', item.id] });
      toast.success('Details saved');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not save details')),
  });

  // Joined values are absent until the request lands; row-carried ones are not.
  const waiting = detailsPending && !detailsFailed;
  const sizeBytes = Number(details?.sizeBytes ?? item?.sizeBytes ?? 0);
  const versionNo = details?.versionNo ?? item?.versionNo;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={item ? `Details — ${item.name}` : 'Details'}
    >
      {item && (
        <>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <Fact label="Type">{fileTypeLabel(details?.mimeType ?? item.mimeType, item.name)}</Fact>

            <Fact label="Size">
              <span className="block">{formatBytes(sizeBytes)}</span>
              <span className="block text-xs text-slate-500">
                {sizeBytes.toLocaleString()} bytes
              </span>
            </Fact>

            <Fact label="Location">
              {waiting ? <Pending /> : folderPathLabel(details?.folderPath)}
            </Fact>

            <Fact label="Uploaded by">
              {waiting ? <Pending /> : <Person person={details?.owner} />}
            </Fact>

            <Fact label="Created">
              <Timestamp iso={details?.createdAt ?? item.createdAt} />
            </Fact>

            <Fact label="Last modified by">
              {waiting ? <Pending /> : <Person person={details?.lastModifiedBy} />}
            </Fact>

            <Fact label="Modified">
              <Timestamp iso={details?.updatedAt ?? item.updatedAt} />
            </Fact>

            <Fact label="Version">
              {waiting ? (
                <Pending />
              ) : (
                <>
                  <span>v{versionNo}</span>
                  {details?.versionCount ? (
                    <span className="text-slate-500">
                      {' · '}
                      {countLabel(details.versionCount, 'version')}
                    </span>
                  ) : null}
                </>
              )}
            </Fact>

            <Fact label="Comments">
              {commentCount ? countLabel(commentCount, 'comment') : 'None'}
            </Fact>

            <Fact label="Sharing">{waiting ? <Pending /> : sharingLabel(details?.shared)}</Fact>
          </dl>

          {detailsFailed && (
            <p className="mt-3 text-xs text-amber-700">
              Could not load ownership, version and sharing details.
            </p>
          )}

          <div className="mt-4 border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              {showAdvanced ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              Advanced
            </button>
            {showAdvanced && (
              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
                <Fact label="File ID">
                  <CopyableValue label="file ID" value={item.id} />
                </Fact>
                <Fact label="Checksum (SHA-256)">
                  {waiting ? (
                    <Pending />
                  ) : details?.checksum ? (
                    <CopyableValue label="checksum" value={details.checksum} />
                  ) : (
                    '—'
                  )}
                </Fact>
              </dl>
            )}
          </div>
        </>
      )}

      <form
        className="mt-5 space-y-4 border-t border-slate-200 pt-5"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Input
          label="Tags"
          placeholder="invoice, q3, finance"
          hint="Comma-separated"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
        />
        <div className="space-y-1">
          <label htmlFor="file-description" className="block text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="file-description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
